import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { ApiAuthorization } from '@paragon/contract-library';
import { userService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { sortBy } from '@paragon/utilities';

import { createScrollbarStyling } from '../createScrollbarStyling';
import { useAppWithThemeSelector } from './hooks';

const DATE_FORMATTING_OPTIONS = { month: 'long', day: 'numeric', year: 'numeric' } as const;

type OperationState = null | { type: 'revoking' | 'renaming'; guid: string } | { type: 'creating'; key: string };

function CopyDialog(props: { keyString: string; onClose: () => void }) {
  const [hasCopied, setHasCopied] = useState(false);

  return (
    <Dialog open onClose={props.onClose}>
      <DialogTitle>Your New API Key</DialogTitle>

      <DialogContent>
        <DialogContentText gutterBottom>
          Click to copy this key to your clipboard. It can be used on any Paragon API route.
        </DialogContentText>

        <Paper sx={[{ width: '100%', overflowX: 'scroll', padding: 1 }, createScrollbarStyling]}>
          <Tooltip title={hasCopied ? 'Copied!' : 'Click to copy'}>
            <Box
              component="pre"
              sx={{
                margin: 0,
                marginX: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
              onPointerEnter={() => setHasCopied(false)}
              onClick={() => {
                navigator.clipboard.writeText(props.keyString);
                setHasCopied(true);
              }}
            >
              {props.keyString}
            </Box>
          </Tooltip>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={props.onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RenameDialog(props: {
  auth: ApiAuthorization;
  onCancel: () => void;
  onSuccess: () => void;
  onFatalError: (error: any) => void;
}) {
  const [isValidating, setIsValidating] = useState(false);
  const [isWorking, setIsWorking] = useState(false);

  const [name, setName] = useState(props.auth.userProvidedName ?? '');

  const textFieldRef = useRef<HTMLInputElement>(null);

  function onSubmit() {
    if (name.trim() === '') {
      setIsValidating(true);
      textFieldRef.current!.focus();
      return;
    }

    setIsWorking(true);

    userService.api.renameApiAuthorization(props.auth.guid, name).then(props.onSuccess).catch(props.onFatalError);
  }

  return (
    <Dialog open>
      <DialogTitle>Rename API Key</DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          inputRef={textFieldRef}
          size="small"
          margin="dense"
          autoComplete="off"
          autoFocus
          required
          error={isValidating && name.trim() === ''}
          label="Key Name"
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              onSubmit();
            }
          }}
        ></TextField>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={props.onCancel} disabled={isWorking}>
          Cancel
        </Button>

        <Button variant="contained" disabled={isWorking} onClick={onSubmit}>
          {isWorking ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RevokeConfirmationDialog(props: {
  auth: ApiAuthorization;
  onCancel: () => void;
  onSuccess: () => void;
  onFatalError: (error: any) => void;
}) {
  const [isWorking, setIsWorking] = useState(false);

  return (
    <Dialog open>
      <DialogTitle>Confirm revoking {props.auth.userProvidedName ?? 'unnamed key'}?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          This key{' '}
          {props.auth.lastUsedAt
            ? `was last used ${new Date(props.auth.lastUsedAt).toLocaleDateString('en-US', DATE_FORMATTING_OPTIONS)}. Any applications using this key will stop working.`
            : 'has never been used.'}{' '}
          Revoking an API key cannot be undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" disabled={isWorking} onClick={props.onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={isWorking}
          color="error"
          onClick={() => {
            setIsWorking(true);
            userService.api.revokeApiAuthorization(props.auth.guid).then(props.onSuccess).catch(props.onFatalError);
          }}
        >
          I understand, revoke this key
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function ApiKeysDialog(props: { onClose: () => void }) {
  const dispatch = useDispatch();

  const user = useAppWithThemeSelector((state) => state.logInData.loggedInUser!);

  const [isWorking, setIsWorking] = useState(false);
  const [operation, setOperation] = useState<OperationState>(null);

  function concludeOperation() {
    setOperation(null);
  }

  function onRenameRequested(guid: string) {
    setOperation({ type: 'renaming', guid });
  }

  function onRevocationRequested(guid: string) {
    setOperation({ type: 'revoking', guid });
  }

  const [authorizations, setAuthorizations] = useState<ApiAuthorization[]>([]);

  function loadAuthorizations() {
    return userService.api
      .getApiAuthorizations()
      .then((authorizations) => authorizations.filter((auth) => !auth.revoked))
      .then(setAuthorizations);
  }

  const reloadAuthorizations = loadAuthorizations;

  useEffect(() => void loadAuthorizations(), []);

  const handleFatalErrorWhile = (operation: string) => (error: any) => {
    console.error(error);
    dispatch(enqueueError(`Error ${operation}. Please contact Paragon Customer Support.`));
    props.onClose();
  };

  return (
    <>
      {operation?.type === 'creating' && <CopyDialog keyString={operation.key} onClose={concludeOperation} />}

      {operation?.type === 'revoking' && (
        <RevokeConfirmationDialog
          auth={authorizations.find((auth) => auth.guid === operation.guid)!}
          onCancel={concludeOperation}
          onSuccess={() => {
            reloadAuthorizations().then(concludeOperation).catch(handleFatalErrorWhile('revoking API key'));
          }}
          onFatalError={handleFatalErrorWhile('revoking API key')}
        />
      )}

      {operation?.type === 'renaming' && (
        <RenameDialog
          auth={authorizations.find((auth) => auth.guid === operation.guid)!}
          onCancel={concludeOperation}
          onSuccess={() => {
            reloadAuthorizations().then(concludeOperation).catch(handleFatalErrorWhile('renaming API key'));
          }}
          onFatalError={handleFatalErrorWhile('renaming API key')}
        />
      )}

      <Dialog open onClose={props.onClose}>
        <DialogTitle>Your API Keys</DialogTitle>

        <DialogContent>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            loading={isWorking}
            loadingPosition="start"
            onClick={() => {
              setIsWorking(true);

              userService.api
                .authorizeApiAccess(user.email)
                .then((result) => {
                  setOperation({ type: 'creating', key: result.jwt });
                  return reloadAuthorizations().finally(() => setIsWorking(false));
                })
                .catch(handleFatalErrorWhile('creating API key'));
            }}
          >
            Create New Key
          </Button>

          <Divider sx={{ marginY: 1 }} />

          <Box sx={[{ overflowY: 'auto', maxHeight: '50vh' }, createScrollbarStyling]}>
            {authorizations.length === 0 ? (
              <Paper variant="outlined" sx={{ marginTop: 1 }}>
                <Typography variant="body1" color="textSecondary" sx={{ margin: 2 }}>
                  No API keys issued yet.
                </Typography>
              </Paper>
            ) : (
              sortBy(
                authorizations,
                [
                  (auth: ApiAuthorization) => new Date(auth.createdAt),
                  (auth: ApiAuthorization) => new Date(auth.lastUsedAt),
                ],
                ['desc', 'desc'],
              ).map((auth) => (
                <Card key={auth.guid} sx={{ margin: 1 }}>
                  <>
                    <CardContent>
                      <Typography variant="h5">{auth.userProvidedName ?? 'unnamed key'}</Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        {auth.lastUsedAt
                          ? `Last used: ${new Date(auth.lastUsedAt).toLocaleDateString('en-US', DATE_FORMATTING_OPTIONS)}`
                          : 'Never used'}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Issued: {new Date(auth.createdAt).toLocaleDateString('en-US', DATE_FORMATTING_OPTIONS)}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button size="small" disabled={operation != null} onClick={() => onRenameRequested(auth.guid)}>
                        {auth.userProvidedName == null ? 'Name' : 'Rename'}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        disabled={operation != null}
                        onClick={() => onRevocationRequested(auth.guid)}
                      >
                        Revoke
                      </Button>
                    </CardActions>
                  </>
                </Card>
              ))
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={props.onClose} disabled={isWorking}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
